import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { BLOCK } from '../../constants/common';
import { Block, Window } from '../common/blocks';
import { Link } from '../common/buttons';
import { addLangPrefix } from '../common/buttons/Link';
import { smoothScrollToTop } from '../../hooks/common/usePersistedPage';
import { Url } from '../common/Url';
import {
    accountRoutes,
    searchRoutes,
    toolsRoutes,
} from '../../constants/routes';
import { BrandName } from './BrandName';
import { toGeLocative } from '../../utils/common/seo/landingTemplates';
import { COLORS } from '../../constants';
import Icon, { iconNames } from '../common/icon';
import { PAGINATION } from '../../constants/common/pagination';
import { setStorageKey } from '../../hooks/useStorageKeyWatcher';
import { saveSearchValues } from '../../redux/reducers/search/searchData';
import { getBrandMeta } from '../../constants/brandMeta';
import { getMailtoHref } from '../../utils/common/contactLinks';

const FOOTER_TOP_CITIES = [
    'tbilisi',
    'batumi',
    'kutaisi',
    'rustavi',
    'gori',
    'zugdidi',
    'poti',
    'kobuleti',
];

const FOOTER_TOP_BRANDS = [
    { slug: 'toyota', name: 'Toyota' },
    { slug: 'mercedes-benz', name: 'Mercedes-Benz' },
    { slug: 'bmw', name: 'BMW' },
    { slug: 'hyundai', name: 'Hyundai' },
    { slug: 'kia', name: 'Kia' },
    { slug: 'lexus', name: 'Lexus' },
    { slug: 'honda', name: 'Honda' },
    { slug: 'nissan', name: 'Nissan' },
];

const CITY_ANCHOR = {
    ge: (n) => `ავტო ${toGeLocative(n)}`,
    ru: (n) => `Купить авто в ${n}`,
    en: (n) => `Buy a car in ${n}`,
    ua: (n) => `Купити авто у ${n}`,
};

const BRAND_ANCHOR = {
    ge: (n) => `${n} საქართველოში`,
    ru: (n) => `Купить ${n} в Грузии`,
    en: (n) => `Buy ${n} in Georgia`,
    ua: (n) => `Купити ${n} у Грузії`,
};

const FOOTER_COLOR = '#EAEAEA';
const LINK_CLASS = 'nav__item url';

const headingStyle = { color: FOOTER_COLOR, fontWeight: '500' };
const hrStyle = {
    backgroundColor: FOOTER_COLOR,
    border: 'none',
    height: '1px',
    width: '100%',
    margin: '1rem 0',
};

const FooterColumn = ({ title, children }) => (
    <Block name={BLOCK.HALF}>
        <h3 style={headingStyle}>{title}</h3>
        <div className="text__footer">
            <Block name={BLOCK.CENTER}>{children}</Block>
        </div>
    </Block>
);

const SOCIAL_LINKS = [
    {
        name: iconNames.facebook,
        label: 'Facebook',
        href: 'https://www.facebook.com/profile.php?id=61579279043468&locale=ru_RU',
    },
    {
        name: iconNames.instagram,
        label: 'Instagram',
        href: 'https://www.instagram.com/worldauto.ge/',
    },
    {
        name: iconNames.youtube,
        label: 'YouTube',
        href: 'https://www.youtube.com/@worldautoge',
    },
    {
        name: iconNames.tiktok,
        label: 'TikTok',
        href: 'https://www.tiktok.com/@worldauto.ge',
    },
    {
        name: iconNames.telegram,
        label: 'Telegram',
        href: 'https://t.me/worldautoge',
    },
];

const SocialLinks = () => (
    <div className="footer__socials">
        {SOCIAL_LINKS.map(({ name, label, href }) => (
            <Url
                key={name}
                to={href}
                target="_blank"
                noTranslate
                aria-label={label}
                text={<Icon name={name} size={30} />}
            />
        ))}
    </div>
);

const PaymentIcons = () => (
    <div
        style={{
            display: 'flex',
            alignItems: 'center',
            margin: '0.5rem 0',
            padding: '0.5rem',
            backgroundColor: COLORS.GRAY_LIGHT,
            borderRadius: '0.5rem',
            gap: '0.5rem',
        }}
    >
        <Icon name={iconNames.visa} />
        <Icon name={iconNames.mastercard} />
    </div>
);

const ContactBlock = ({ t }) => (
    <Block name={BLOCK.HALF}>
        <h3 style={headingStyle}>{t('help')}</h3>
        <p className="text__footer">{t('companyGeName')}</p>
        <p className="text__footer">
            {t('advertstingPartner')}
            <Url
                className={LINK_CLASS}
                style={{ margin: '0 0.5rem' }}
                to={getMailtoHref('world.auto.ge@gmail.com')}
                text="world.auto.ge@gmail.com"
                noTranslate
            />
        </p>
        <p className="text__footer">
            {t('contactUs')}
            <Url
                className={LINK_CLASS}
                style={{ margin: '0 0.5rem' }}
                to={getMailtoHref('world.auto.ge@gmail.com')}
                text="world.auto.ge@gmail.com"
                noTranslate
            />
        </p>
        <p className="text__footer">
            <Link
                to="/about"
                className={LINK_CLASS}
                text={t('aboutPageTitle')}
            />
        </p>
        <p className="text__footer">
            <Link
                to="/contacts"
                className={LINK_CLASS}
                text={t('contactsPageTitle')}
            />
        </p>
        <p className="text__footer">
            <Link
                to={accountRoutes.PARTNERS}
                className={LINK_CLASS}
                text={t('partnerEyebrow')}
            />
        </p>
        <p className="text__footer">
            <Link
                to="/privacy"
                className={LINK_CLASS}
                text={t('titlePrivacyPolicy')}
            />
        </p>
        <p className="text__footer">
            <Link
                to="/terms"
                className={LINK_CLASS}
                text={t('titleTermsCondition')}
            />
        </p>
        <PaymentIcons />
    </Block>
);

const HIDE_ON_ROUTE_PATTERNS = [
    /\/account\/(login|signup|forgot|new\/password)/,
    /\/auth\//,
];

const Footer = () => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const { pathname } = useLocation();
    const lang = i18n.language || 'ge';
    const cityAnchor = CITY_ANCHOR[lang] || CITY_ANCHOR.en;
    const brandAnchor = BRAND_ANCHOR[lang] || BRAND_ANCHOR.en;

    if (HIDE_ON_ROUTE_PATTERNS.some((re) => re.test(pathname))) {
        return null;
    }

    const handleSellCityClick = (slug) => {
        setStorageKey(PAGINATION.SELL_CAR_LIST);
        dispatch(saveSearchValues({ city: [slug] }));
        smoothScrollToTop();
    };

    const handleSellBrandClick = (brandSlug) => {
        setStorageKey(PAGINATION.SELL_CAR_LIST);
        const meta = getBrandMeta(brandSlug);
        if (meta?.dbName) {
            dispatch(saveSearchValues({ mark: meta.dbName }));
        }
        smoothScrollToTop();
    };

    const handleRentCityClick = (slug) => {
        setStorageKey(PAGINATION.RENT_CAR_LIST);
        dispatch(saveSearchValues({ city: [slug] }));
        smoothScrollToTop();
    };

    return (
        <>
            <hr style={hrStyle} />
            <Window isDark>
                <div className="container">
                    <FooterColumn title={t('linksCityCarSell')}>
                        {FOOTER_TOP_CITIES.map((slug) => {
                            const cityName = t(slug);
                            const anchor = cityAnchor(cityName);
                            return (
                                <RouterLink
                                    key={`footer-city-${slug}`}
                                    className={LINK_CLASS}
                                    to={addLangPrefix(
                                        `${searchRoutes.CAR}/${slug}`,
                                    )}
                                    onClick={() => handleSellCityClick(slug)}
                                    title={anchor}
                                    aria-label={anchor}
                                >
                                    {cityName}
                                </RouterLink>
                            );
                        })}
                    </FooterColumn>
                    <FooterColumn title={t('linksBrandCar')}>
                        {FOOTER_TOP_BRANDS.map((brand) => {
                            const anchor = brandAnchor(brand.name);
                            return (
                                <RouterLink
                                    key={`footer-brand-${brand.slug}`}
                                    className={LINK_CLASS}
                                    to={addLangPrefix(
                                        `${searchRoutes.CAR}/brand/${brand.slug}`,
                                    )}
                                    onClick={() =>
                                        handleSellBrandClick(brand.slug)
                                    }
                                    title={anchor}
                                    aria-label={anchor}
                                >
                                    {brand.name}
                                </RouterLink>
                            );
                        })}
                    </FooterColumn>
                </div>
            </Window>

            <hr style={hrStyle} />

            <Window isDark>
                <div className="container">
                    <FooterColumn title={t('foundRent')}>
                        {FOOTER_TOP_CITIES.slice(0, 5).map((slug) => {
                            const cityName = t(slug);
                            return (
                                <RouterLink
                                    key={`footer-rent-${slug}`}
                                    className={LINK_CLASS}
                                    to={addLangPrefix(
                                        `${searchRoutes.RENT_CAR}/${slug}`,
                                    )}
                                    onClick={() => handleRentCityClick(slug)}
                                    title={cityName}
                                    aria-label={cityName}
                                >
                                    {cityName}
                                </RouterLink>
                            );
                        })}
                    </FooterColumn>
                    <FooterColumn title={t('parts')}>
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.PARTS_CAR}
                            text={t('cars')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.PARTS_BIKE}
                            text={t('bikes')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.PARTS_COMMERCIAL}
                            text={t('commercials')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.PARTS}
                            text={t('equipment')}
                        />
                    </FooterColumn>
                </div>
            </Window>

            <hr style={hrStyle} />

            <Window isDark>
                <div className="container">
                    <ContactBlock t={t} />
                    <FooterColumn title={t('navigationSite')}>
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.CAR}
                            text={t('cars')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.BIKE}
                            text={t('bikes')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.COMMERCIAL}
                            text={t('commercials')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.CHECKAUTO}
                            text={t('checkauto')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.RENT_CAR}
                            text={t('foundRent')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.SERVICE}
                            text={t('services')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.PLACEMENT}
                            text={t('placements')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={searchRoutes.PARTS_CAR}
                            text={t('parts')}
                        />
                        <Link
                            className={LINK_CLASS}
                            to={toolsRoutes.MARKET}
                            text={t('marketNavTitle')}
                        />
                        <p className="text__footer">
                            <Link
                                to={toolsRoutes.CUSTOMS_CALCULATOR}
                                className={LINK_CLASS}
                                text={t('calculatorPageTitle')}
                            />
                        </p>
                    </FooterColumn>
                </div>
            </Window>
            <hr style={hrStyle} />
            <Window isDark>
                <p className="text__footer">Worldauto.ge {t('aboutUs-1')}</p>
            </Window>
            <Block name={BLOCK.COLUMN}>
                <SocialLinks />
                <p className="text__footer">
                    © {new Date().getFullYear()} "Dom Group LLC".{' '}
                    {t('rightReserved')}
                </p>
                <p className="text__footer">
                    2017 - {new Date().getFullYear()}
                </p>
            </Block>
            <Block name={BLOCK.CENTER}>
                <Link to="/" text={<BrandName />} />
            </Block>
        </>
    );
};

export default Footer;
